import { UserAvatar } from "@/components/shared";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { TooltipContent } from "@/components/ui/tooltip";
import { UserOnEntity } from "@/graphql/__generated__/graphql";
// import { escapeRegExp } from "./utils";

/**
 * Transforms a chat message by detecting user mentions (formatted as "@FirstName LastName")
 * and wrapping them with a Tooltip component that displays extra user information.
 *
 * @param {string} message - The input message that may include user mentions.
 * @param {UserOnEntity[]} tenderUsers - An array of user objects used to identify and render mentions.
 * @returns {React.ReactNode[]} An array of React nodes representing the message parts with formatted mentions.
 */
const ChatMessageWithMentions = (message: string, tenderUsers: UserOnEntity[]): React.ReactNode[] => {
  // Create a map from user id to user for efficient lookup
  const userMap = new Map(
    tenderUsers.map(u => [u.id, u])
  );

  // Use a generic regex that matches tokens of the form "@User:<digits>"
  // This regex works irrespective of the length of the id.
  const mentionRegex = /(@User:\d+\b)/g;

  return message.split(mentionRegex).map((part, index) => {
    if (part.startsWith("@User:")) {
      const idStr = part.slice(6); // Remove the "@User:" prefix
      const userId = parseInt(idStr, 10);
      const user = userMap.get(userId);
      if (user) {
        return (
          <Tooltip key={index} delayDuration={100}>
            <TooltipTrigger asChild>
              <span className="text-blue-600 cursor-pointer">{`@${user.firstname} ${user.lastname}`}</span>
            </TooltipTrigger>
            <TooltipContent className="p-2">
              <div className="flex items-center gap-2">
                <UserAvatar user={user as any} />
                <div>
                  <p className="text-sm font-medium">
                    {user.firstname} {user.lastname}
                  </p>
                  {user.organization && (
                    <p className="text-xs text-slate-500">
                      {user.organization.shortName}
                    </p>
                  )}
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        );
      }
    }
    return part;
  });
};

export default ChatMessageWithMentions;
